"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { MessageSquare, Target, Beaker, GraduationCap, Sparkles, FileText, CalendarRange } from "lucide-react";
import { ErrorMessage } from "@/components/ui/error-message";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useProjectAnalytics } from "@/hooks/useProjectAnalytics";
import { AnalyticsStatTile } from "@/components/analytics/analytics-stat-tile";
export interface ProjectAnalyticsTilesProps {
  projectId: string;
}

/**
 * Project analytics tile grid (all-time counts + median learning cycle).
 * Shared by the header pulse popover.
 */
export function ProjectAnalyticsTiles(t0) {
  const $ = _c(45);
  const {
    projectId
  } = t0;
  const analytics = useProjectAnalytics(projectId);
  const [tooltipsEnabled, setTooltipsEnabled] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const timeout = window.setTimeout(() => setTooltipsEnabled(true), 1500);
      return () => window.clearTimeout(timeout);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== projectId) {
    t2 = [projectId];
    $[1] = projectId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC" : "UTC";
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const browserTz = t3;
  const medianValue = analytics.loading ? "\u2026" : analytics.medianLearningCycleDays == null ? "\u2014" : `${analytics.medianLearningCycleDays} days`;
  const medianSubtitle = analytics.loading ? "Loading\u2026" : `Whole calendar days in ${browserTz} from earliest linked assumption to learning date.`;
  let t4;
  bb0: {
    if (analytics.loading) {
      t4 = undefined;
      break bb0;
    }
    if (analytics.learningCycleSampleSize > 0) {
      const n = analytics.learningCycleSampleSize;
      t4 = `Based on ${n} learning${n === 1 ? "" : "s"} with linked assumptions.`;
      break bb0;
    }
    if (analytics.learningsSynthesized > 0) {
      t4 = "No qualifying learnings yet (hypothesis needs linked assumptions).";
      break bb0;
    }
    t4 = "Shown when learnings include assumptions on their hypotheses.";
  }
  const medianDetail = t4;
  let t5;
  bb1: {
    if (analytics.loading) {
      t5 = "Loading\u2026";
      break bb1;
    }
    const base = `For each qualifying learning we use the earliest assumption on its hypothesis and the learning timestamp, count whole calendar days between them in ${browserTz} (your browser zone, sent to the API), then take the median.`;
    if (analytics.learningCycleSampleSize > 0) {
      const n_0 = analytics.learningCycleSampleSize;
      t5 = `${base} Sample size: ${n_0} learning${n_0 === 1 ? "" : "s"}.`;
      break bb1;
    }
    t5 = base;
  }
  const medianTooltip = t5;
  if (analytics.error) {
    let t6;
    if ($[4] !== analytics.error) {
      t6 = <ErrorMessage message={analytics.error} />;
      $[4] = analytics.error;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    return t6;
  }
  const t6 = !tooltipsEnabled;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CalendarRange className="h-4 w-4" />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = tooltipsEnabled ? medianTooltip : undefined;
  let t9;
  if ($[7] !== medianDetail || $[8] !== medianSubtitle || $[9] !== medianValue || $[10] !== t8) {
    t9 = <AnalyticsStatTile highlight={true} className="sm:col-span-2" title="Median learning cycle" value={medianValue} icon={t7} subtitle={medianSubtitle} detail={medianDetail} methodologyTooltip={t8} methodologyAriaLabel="How median learning cycle is calculated" />;
    $[7] = medianDetail;
    $[8] = medianSubtitle;
    $[9] = medianValue;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = analytics.loading ? "\u2026" : analytics.chatsStarted;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <MessageSquare className="h-4 w-4" />;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10) {
    t12 = <AnalyticsStatTile uniformHeight={true} title="Chats started" value={t10} icon={t11} />;
    $[13] = t10;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = analytics.loading ? "\u2026" : analytics.selectedAssumptions;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Target className="h-4 w-4" />;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t13) {
    t15 = <AnalyticsStatTile uniformHeight={true} title="Assumptions" value={t13} icon={t14} />;
    $[16] = t13;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const t16 = analytics.loading ? "\u2026" : analytics.selectedExperiments;
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Beaker className="h-4 w-4" />;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  const t18 = tooltipsEnabled ? "Counts experiments that are not in Draft status (same rule as the section note above)." : undefined;
  let t19;
  if ($[19] !== t16 || $[20] !== t18) {
    t19 = <AnalyticsStatTile uniformHeight={true} title="Experiments" value={t16} icon={t17} titleTooltip={t18} />;
    $[19] = t16;
    $[20] = t18;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const t20 = analytics.loading ? "\u2026" : analytics.learningsSynthesized;
  let t21;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <GraduationCap className="h-4 w-4" />;
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  const t22 = tooltipsEnabled ? "Synthesized learning cards in this project (excludes deleted learnings)." : undefined;
  let t23;
  if ($[23] !== t20 || $[24] !== t22) {
    t23 = <AnalyticsStatTile uniformHeight={true} title="Learnings" value={t20} icon={t21} titleTooltip={t22} />;
    $[23] = t20;
    $[24] = t22;
    $[25] = t23;
  } else {
    t23 = $[25];
  }
  const t24 = analytics.loading ? "\u2026" : analytics.insightsGenerated;
  let t25;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Sparkles className="h-4 w-4" />;
    $[26] = t25;
  } else {
    t25 = $[26];
  }
  const t26 = tooltipsEnabled ? "Insights extracted from learning cards in this project (excludes deleted insights)." : undefined;
  let t27;
  if ($[27] !== t24 || $[28] !== t26) {
    t27 = <AnalyticsStatTile uniformHeight={true} title="Insights" value={t24} icon={t25} titleTooltip={t26} />;
    $[27] = t24;
    $[28] = t26;
    $[29] = t27;
  } else {
    t27 = $[29];
  }
  const t28 = analytics.loading ? "\u2026" : analytics.documentsAdded;
  let t29;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <FileText className="h-4 w-4" />;
    $[30] = t29;
  } else {
    t29 = $[30];
  }
  const t30 = tooltipsEnabled ? "Documents uploaded or linked to this project." : undefined;
  let t31;
  if ($[31] !== t28 || $[32] !== t30) {
    t31 = <AnalyticsStatTile uniformHeight={true} title="Documents" value={t28} icon={t29} titleTooltip={t30} />;
    $[31] = t28;
    $[32] = t30;
    $[33] = t31;
  } else {
    t31 = $[33];
  }
  let t32;
  if ($[34] !== t12 || $[35] !== t15 || $[36] !== t19 || $[37] !== t23 || $[38] !== t27 || $[39] !== t31 || $[40] !== t9) {
    t32 = <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">{t9}{t12}{t15}{t19}{t23}{t27}{t31}</div>;
    $[34] = t12;
    $[35] = t15;
    $[36] = t19;
    $[37] = t23;
    $[38] = t27;
    $[39] = t31;
    $[40] = t9;
    $[41] = t32;
  } else {
    t32 = $[41];
  }
  let t33;
  if ($[42] !== t32 || $[43] !== t6) {
    t33 = <TooltipProvider delayDuration={250} disableHoverableContent={t6}>{t32}</TooltipProvider>;
    $[42] = t32;
    $[43] = t6;
    $[44] = t33;
  } else {
    t33 = $[44];
  }
  return t33;
}